import Preferences from "action/Preferences";
import modalStore from "component/data/modalStore";
import context from "data/context/Context";
import Strings from "wasabi-common/lib/types/Strings";

export abstract class NativeBridge {
  protected _version = null;

  public subscribeToTopic(): boolean {
    return context.currentOwner && !Preferences.hasBackUp();
  }

  public unsubscribeToTopic() {
    return context.currentOwner && !Preferences.hasBackUp();
  }

  public onUpdateDeviceList(_message?: unknown): void {}

  public onMessageReceived(content: string) {
    const message = JSON.parse(content);
    const type = message.type || "message";
    const body = message.body;
    const title = message.title;
    if ("message" === type) {
      modalStore.show(title, body, [
        {
          name: "ok",
          text: "ok",
          color: "#ff7c00",
        },
      ]);
    }
    return true;
  }

  public subscribedToTopic(versionName: string) {
    if (context.currentOwner && !Preferences.hasBackUp()) {
      this._version = versionName;
    }
  }

  public openExternalUrl(link: string) {
    return Strings.has(link);
  }

  public openShareDialog(link: string) {
    return false;
  }

  public openShareDialogInstagram(link: string) {
    return false;
  }

  public requestAvailableNetworks(): boolean {
    return false;
  }

  public serverStart(ssid: string, password: string): boolean {
    return false;
  }

  public serverStop() {
    return false;
  }

  public isSupportGoogleSign() {
    return true;
  }

  public isSupportLoginFacebook() {
    return false;
  }

  public loginFacebook() {
    return false;
  }

  public get version(): string {
    return this._version;
  }

  public isSupportAnimatedContent() {
    return true;
  }

  public isSupportShare() {
    return false;
  }
}

class AndroidNativeBridge extends NativeBridge {
  public subscribeToTopic(): boolean {
    if (super.subscribeToTopic()) {
      if (currentWindow.JavaScriptReceiver.subscribeToTopic) {
        currentWindow.JavaScriptReceiver.subscribeToTopic(context.currentOwner.oid);
        return true;
      }
    }
    return false;
  }

  public unsubscribeToTopic(): boolean {
    if (super.unsubscribeToTopic()) {
      if (currentWindow.JavaScriptReceiver.unsubscribeToTopic) {
        currentWindow.JavaScriptReceiver.unsubscribeToTopic(context.currentOwner.oid);
        return true;
      }
    }
    return false;
  }

  public openExternalUrl(link: string): boolean {
    if (super.openExternalUrl(link)) {
      if (currentWindow.JavaScriptReceiver.openExternalUrl) {
        currentWindow.JavaScriptReceiver.openExternalUrl(link);
        return true;
      }
      window.open(link, "_blank");
      return true;
    }
    return false;
  }

  public requestAvailableNetworks(): boolean {
    return currentWindow.JavaScriptReceiver.requestAvailableNetworks();
  }

  public serverStart(ssid: string, password: string): boolean {
    return currentWindow.JavaScriptReceiver.serverStart(ssid, password);
  }

  public serverStop(): boolean {
    return currentWindow.JavaScriptReceiver.serverStop();
  }

  public openShareDialog(link: string): boolean {
    if (currentWindow.JavaScriptReceiver.openShareDialog) {
      currentWindow.JavaScriptReceiver.openShareDialog(link);
      return true;
    }
    return false;
  }

  public openShareDialogInstagram(link: string): boolean {
    if (currentWindow.JavaScriptReceiver.openShareDialogInstagram) {
      currentWindow.JavaScriptReceiver.openShareDialogInstagram(link);
      return true;
    }
    return this.openShareDialog(link);
  }

  public isSupportGoogleSign() {
    return false;
  }

  public isSupportLoginFacebook() {
    if (currentWindow.JavaScriptReceiver.loginFacebook) {
      return true;
    }
    return false;
  }

  public loginFacebook() {
    if (currentWindow.JavaScriptReceiver.loginFacebook) {
      currentWindow.JavaScriptReceiver.loginFacebook();
      return true;
    }
    return false;
  }

  public isSupportShare() {
    if (currentWindow.JavaScriptReceiver.openShareDialog) {
      return true;
    }
    return false;
  }
}

class IOSNativeBridge extends NativeBridge {
  public subscribeToTopic() {
    if (super.subscribeToTopic()) {
      if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.subscribeToTopic) {
        currentWindow.webkit.messageHandlers.subscribeToTopic.postMessage(context.currentOwner.oid);
        return true;
      }
    }
    return false;
  }

  public unsubscribeToTopic(): boolean {
    if (super.unsubscribeToTopic()) {
      if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.unsubscribeToTopic) {
        currentWindow.webkit.messageHandlers.unsubscribeToTopic.postMessage(context.currentOwner.oid);
        return true;
      }
    }
    return false;
  }

  public openExternalUrl(link: string): boolean {
    if (super.openExternalUrl(link)) {
      if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.openExternalUrl) {
        currentWindow.webkit.messageHandlers.openExternalUrl.postMessage(link);
        return true;
      }
    }
    return false;
  }

  public requestAvailableNetworks(): boolean {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.requestAvailableNetworks) {
      currentWindow.webkit.messageHandlers.requestAvailableNetworks.postMessage("get");
      return true;
    }

    return false;
  }

  public serverStart(ssid: string, password: string): boolean {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.serverStart) {
      const message = {
        ssid,
        password,
      };
      currentWindow.webkit.messageHandlers.serverStart.postMessage(JSON.stringify(message));
      return true;
    }
    return false;
  }

  public openShareDialog(link: string): boolean {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.openShareDialog) {
      currentWindow.webkit.messageHandlers.openShareDialog.postMessage(link);
      return true;
    }
    return false;
  }

  public openShareDialogInstagram(link: string): boolean {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.openShareDialogInstagram) {
      currentWindow.webkit.messageHandlers.openShareDialogInstagram.postMessage(link);
      return true;
    }
    return this.openShareDialog(link);
  }

  public isSupportGoogleSign() {
    return false;
  }

  public isSupportLoginFacebook() {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.loginFacebook) {
      return true;
    }
    return false;
  }

  public loginFacebook() {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.loginFacebook) {
      currentWindow.webkit.messageHandlers.loginFacebook.postMessage("login");
      return true;
    }
    return false;
  }

  public isSupportAnimatedContent() {
    try {
      if (Preferences.hasBackUp()) {
        return true;
      }
      if (!this._version) {
        return false;
      }

      return this._version.localeCompare("1.53") >= 0;
    } catch (error) {
      return false;
    }
  }

  public isSupportShare() {
    if (currentWindow.webkit.messageHandlers && currentWindow.webkit.messageHandlers.openShareDialog) {
      return true;
    }
    return false;
  }
}

class BrowserBridge extends NativeBridge {
  public subscribeToTopic() {
    return false;
  }

  public unsubscribeToTopic(): boolean {
    return false;
  }

  public openExternalUrl(link: string): boolean {
    if (super.openExternalUrl(link)) {
      window.open(link, "_blank");
      return true;
    }
    return false;
  }
}

let nativeBridge: NativeBridge;

const currentWindow = window as any;

if (typeof currentWindow.JavaScriptReceiver !== "undefined") {
  nativeBridge = new AndroidNativeBridge();
} else if (typeof currentWindow.webkit !== "undefined") {
  nativeBridge = new IOSNativeBridge();
} else {
  nativeBridge = new BrowserBridge();
}

(window as any).NativeInterface = nativeBridge;
export default nativeBridge;
